import React, { useState } from 'react';

export interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  errorMessage?: string | null;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'danger' | 'primary';
  showNotesField?: boolean;
  notesLabel?: string;
  notesPlaceholder?: string;
  onConfirm: (notes?: string) => void;
  onCancel: () => void;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  errorMessage,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  showNotesField = false,
  notesLabel = 'Notes',
  notesPlaceholder = 'Add notes (optional)...',
  onConfirm,
  onCancel,
}) => {
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const confirmButtonClass =
    confirmVariant === 'danger'
      ? 'bg-brand-warm hover:bg-brand-warm-deep'
      : 'bg-brand-primary hover:bg-brand-primary-hover';

  const handleConfirm = () => {
    onConfirm(showNotesField ? notes : undefined);
    setNotes(''); // Reset notes after confirmation
  };

  const handleCancel = () => {
    setNotes(''); // Reset notes on cancel
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg p-6 w-full max-w-md shadow-xl">
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>

        <p className="text-white/70 mb-4">{message}</p>

        {errorMessage && (
          <div className="mb-4 p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
            <p className="text-sm text-brand-warm">{errorMessage}</p>
          </div>
        )}

        {showNotesField && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-white/70 mb-2">
              {notesLabel}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder={notesPlaceholder}
              rows={3}
            />
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors font-medium"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-semibold ${confirmButtonClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
